import React, {useState, useMemo} from 'react';
import {useNavigate} from 'react-router-dom';
import {
  Box,
  Button,
  Grid,
  Container,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  FormControl,
} from '@mui/material';
import {useTheme} from '@mui/material/styles';
import {AutoFillAwareTextField} from '../base';
import {postForgotPassword} from '../../../api/auth';
import {isEmailValid} from '../../../utils/miscellaneous';
import {delay} from '../../../utils/miscellaneous';

const secondsToWaitForSendingNewLink = 60;

/*
 * Handles forgot password functionality. A security code is sent to
 * the email user entered associated with his/her account
 */
export const ForgotPassword = () => {
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [email, setEmail] = useState('');
  const [open, setOpen] = useState(true);
  const [sendNewLink, setSendNewLink] = useState(true);
  const navigate = useNavigate();

  const theme = useTheme();
  const dialogStyle = {
    '& .MuiPaper-root': {
      borderRadius: '20px',
    },
    position: 'absolute',
    top: theme.spacing(5),
    paddingY: theme.spacing(2),
  };

  const formIsFilled = useMemo(() => {
    return email !== '';
  }, [email]);

  const handleForgotPassword = async () => {
    setSendNewLink(false);
    //Clear error and success messages
    setError('');
    setSuccess('');

    // validate email
    if (!isEmailValid(email)) {
      setError('Email is not valid.');
      setSendNewLink(true);
      return;
    }

    await postForgotPassword(email)
      .catch(error => {
        setSuccess('');
        setError('Something went wrong. Please try again.');
        setSendNewLink(true);
      })
      .then(res => {
        if (res.status == 200) {
          setError('');
          setSuccess(
            "Please check your email box for a link to reset your password. The link may take up to 2 minutes to be delivered. Don't forget to check your spam folder as well.",
          );
        }
      });

    delay(secondsToWaitForSendingNewLink).then(() => {
      setSendNewLink(true);
    });
  };

  return (
    <Box
      sx={{
        minHeight: 'calc(100vh)',
        backgroundColor: '#f9f9f9',
        width: '100%',
      }}
    >
      <Container component="main" maxWidth="xs">
        <Dialog open={open} maxWidth="sm" sx={dialogStyle}>
          <DialogTitle>
            <Typography component="span" variant="h6" fontWeight={'bold'}>
              Forgot your password?
            </Typography>
          </DialogTitle>
          <DialogContent sx={{paddingY: theme.spacing(3)}}>
            <Typography variant="subtitle2">
              Please enter the email associated with your account to get a password reset code.
            </Typography>

            <FormControl fullWidth>
              <AutoFillAwareTextField
                autoComplete="new-email"
                margin="normal"
                label="Email"
                variant="outlined"
                value={email}
                onChange={e => {
                  setEmail(e.target.value);
                }}
                fullWidth
              />
            </FormControl>
            {error ? (
              <Grid container justifyContent="flex-end">
                <Grid item xs>
                  <Box display="flex" justifyContent="center">
                    <Alert variant="outlined" severity="error">
                      {error}
                    </Alert>
                  </Box>
                </Grid>
              </Grid>
            ) : (
              ''
            )}
            {success ? (
              <Grid container justifyContent="flex-end">
                <Grid item xs sx={{justifyContent: 'center'}}>
                  <Box display="flex" justifyContent="center">
                    <Alert variant="outlined" severity="success">
                      {success}
                    </Alert>
                  </Box>
                </Grid>
              </Grid>
            ) : (
              ''
            )}
          </DialogContent>

          <DialogActions
            sx={{
              paddingBottom: theme.spacing(2),
              paddingX: theme.spacing(3),
              display: 'flex',
              justifyContent: 'space-between',
            }}
          >
            <Button variant="text" onClick={() => navigate(-1)}>
              Cancel
            </Button>
            <Button
              variant="text"
              color="primary"
              disabled={!formIsFilled || !sendNewLink}
              onClick={handleForgotPassword}
            >
              Send Request
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  );
};
